import type Redis from 'ioredis'

// Types for API responses
export interface FootballMatch {
  match_id: number
  match_date: string
  match_time: string
  status: string
  competition_id: number
  competition_name: string
  competition_emblem: string
  home_team_name: string
  home_team_crest: string
  away_team_name: string
  away_team_crest: string
  home_score: number | null
  away_score: number | null
  home_ht_score: number | null
  away_ht_score: number | null
  priority: number
  home_yellow_cards: number
  away_yellow_cards: number
  home_red_cards: number
  away_red_cards: number
  home_corners: number
  away_corners: number
}

export interface GroupedMatches {
  [competitionId: string]: {
    id: number
    name: string
    logo: string
    priority: number
    matches: FootballMatch[]
  }
}

export interface FootballApiResponse {
  data: FootballMatch[]
  has_more: boolean
  total: number
  page: number
  per_page: number
}

export interface ApiFootballFixture {
  fixture: {
    id: number
    date: string
    status: {
      short: string
    }
    venue?: {
      id: number | null
      name: string | null
      city: string | null
    }
  }
  league: {
    id: number
    name: string
    logo: string
  }
  teams: {
    home: {
      id: number
      name: string
      logo: string
    }
    away: {
      id: number
      name: string
      logo: string
    }
  }
  goals: {
    home: number | null
    away: number | null
  }
  score?: {
    halftime: {
      home: number | null
      away: number | null
    }
    fulltime: {
      home: number | null
      away: number | null
    }
    extratime?: {
      home: number | null
      away: number | null
    }
    penalty?: {
      home: number | null
      away: number | null
    }
  }
  statistics?: Array<{
    team: {
      id: number
    }
    statistics: Array<{
      type: string
      value: string | number | null
    }>
  }>
  events?: Array<{
    type: string
    detail: string
    team: {
      id: number
    }
  }>
}

// Client-safe utility functions
export function getStatusText(status: string): string {
  const statusTexts: Record<string, string> = {
    'NS': 'Chưa đá',
    '1H': 'HIỆP 1',
    '2H': 'HIỆP 2',
    'HT': 'GIỮA HIỆP',
    'FT': 'Kết thúc',
    'AET': 'Sau hiệp phụ',
    'PEN': 'Sau luân lưu',
    'PST': 'Hoãn lại',
    'SUSP': 'Tạm dừng',
    'CANC': 'Hủy bỏ',
    'ABD': 'Bỏ dở',
    'AWD': 'Xử thắng',
    'WO': 'Đối thủ bỏ cuộc',
    'LIVE': 'ĐANG ĐÁ'
  }
  return statusTexts[status] || status
}

// Server-side only class
export class FootballApiHandler {
  private apiKey = 'e7337a8a6e24bae626238cf33be0fde8'
  private apiBaseUrl = 'https://v3.football.api-sports.io/'
  private redis: Redis | null = null
  private cacheTime = 3600 // 1 hour in seconds

  constructor() {
    // Only initialize Redis on server side
    if (typeof window === 'undefined') {
      try {
        const Redis = require('ioredis')
        this.redis = new Redis({
          host: process.env.REDIS_HOST || 'localhost',
          port: parseInt(process.env.REDIS_PORT || '6379'),
          password: process.env.REDIS_PASSWORD || undefined,
          maxRetriesPerRequest: 3,
          lazyConnect: true,
          retryDelayOnFailover: 100,
          retryDelayOnClusterDown: 300,
          onlyTrackReconnection: false
        })

        // Handle Redis connection errors silently
        if (this.redis) {
          this.redis.on('error', (error) => {
            console.warn('Redis connection error in FootballApiHandler:', error.message)
          })
        }
      } catch (error) {
        console.warn('Failed to initialize Redis in FootballApiHandler:', error)
        this.redis = null
      }
    }
  }

  private async callApi(endpoint: string, params: Record<string, any> = {}): Promise<ApiFootballFixture[] | null> {
    const url = new URL(endpoint, this.apiBaseUrl)
    
    // Add parameters to URL
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        url.searchParams.append(key, value.toString())
      }
    })

    // Add delay to avoid rate limiting
    await new Promise(resolve => setTimeout(resolve, 200))

    try {
      const response = await fetch(url.toString(), {
        headers: {
          'x-rapidapi-key': this.apiKey,
          'x-rapidapi-host': 'v3.football.api-sports.io'
        },
        next: { revalidate: 0 } // Don't cache at Next.js level
      })

      if (!response.ok) {
        console.error('API request failed:', response.status, response.statusText)
        return null
      }

      const data = await response.json()
      
      if (data.response) {
        return data.response as ApiFootballFixture[]
      }

      return null
    } catch (error) {
      console.error('API call error:', error)
      return null
    }
  }

  private getLeaguePriority(leagueId: number): number {
    const priorityLeagues: Record<number, number> = {
      // Top 5 châu Âu - ưu tiên cao nhất
      39: 1,   // Premier League (Anh)
      140: 2,  // La Liga (Tây Ban Nha)
      78: 3,   // Bundesliga (Đức)
      135: 4,  // Serie A (Ý)
      61: 5,   // Ligue 1 (Pháp)
      
      // Các cúp châu Âu ưu tiên cao thứ 2
      2: 10,   // Champions League
      3: 11,   // Europa League
      848: 12, // Conference League
      
      // Các giải lớn khác ưu tiên thứ 3
      88: 20,  // Eredivisie (Hà Lan)
      94: 21,  // Jupiler Pro League (Bỉ)
      106: 22, // Primeira Liga (Bồ Đào Nha)
      188: 23, // Super Lig (Thổ Nhĩ Kỳ)
      
      // Các giải hạng 2 ở châu Âu
      40: 30,  // Championship (Anh hạng Nhất)
      141: 31, // La Liga 2 (Tây Ban Nha hạng Nhất)
      79: 32,  // Bundesliga 2 (Đức hạng Nhất)
      136: 33, // Serie B (Ý hạng Nhất)
      62: 34,  // Ligue 2 (Pháp hạng Nhất)
      
      // Các giải châu Á
      510: 40, // V-League (Việt Nam)
      307: 41, // Saudi Pro League (Saudi Arabia)
      98: 42,  // J1 League (Nhật Bản)
      292: 43, // K-League (Hàn Quốc)
      169: 44, // Chinese Super League (Trung Quốc)
      
      // Giải Nam Mỹ
      128: 50, // Primera Division (Argentina)
      71: 51,  // Brasilerao Serie A (Brazil)
      265: 52, // Primera A (Colombia)
      269: 53, // Primera Division (Chile)
      281: 54, // Primera Division (Uruguay)
      
      // Giải Bắc Mỹ
      253: 60, // Major League Soccer (MLS)
      262: 61, // Liga MX (Mexico)
      
      // Giải châu Phi
      202: 70, // Premier Soccer League (Nam Phi)
      200: 71, // Nigeria Professional Football League
      204: 72, // Botola Pro (Morocco)
    }

    return priorityLeagues[leagueId] || 999
  }

  private async getMatchStatistics(matchId: number): Promise<any> {
    if (!this.redis) return null
    
    const cacheKey = `football_match_stats_${matchId}`
    
    try {
      // Check cache first
      const cached = await this.redis.get(cacheKey)
      if (cached) {
        return JSON.parse(cached)
      }

      // Call API
      const response = await this.callApi('fixtures/statistics', { fixture: matchId })
      
      if (response) {
        // Cache for 1 hour
        await this.redis.setex(cacheKey, 3600, JSON.stringify(response))
        return response
      }
    } catch (error) {
      console.error('Error getting match statistics:', error)
    }

    return null
  }

  private async getMatchEvents(matchId: number): Promise<any> {
    if (!this.redis) return null
    
    const cacheKey = `football_match_events_${matchId}`
    
    try {
      // Check cache first
      const cached = await this.redis.get(cacheKey)
      if (cached) {
        return JSON.parse(cached)
      }

      // Call API
      const response = await this.callApi('fixtures/events', { fixture: matchId })
      
      if (response) {
        // Cache for 1 hour
        await this.redis.setex(cacheKey, 3600, JSON.stringify(response))
        return response
      }
    } catch (error) {
      console.error('Error getting match events:', error)
    }

    return null
  }

  private formatMatches(matches: ApiFootballFixture[]): FootballMatch[] {
    return matches.map(match => {
      // API đã trả về múi giờ +7 sẵn, không cần chuyển đổi
      const matchDateTime = new Date(match.fixture.date)

      // Determine priority for league
      const priority = this.getLeaguePriority(match.league.id)

      // Initialize default values for statistics
      let homeYellowCards = 0
      let awayYellowCards = 0
      let homeRedCards = 0
      let awayRedCards = 0
      let homeCorners = 0
      let awayCorners = 0

      // Process statistics data if available
      if (match.statistics && match.statistics.length > 0) {
        match.statistics.forEach(teamStats => {
          const isHome = teamStats.team.id === match.teams.home.id
          
          teamStats.statistics.forEach(stat => {
            const statValue = typeof stat.value === 'number' ? stat.value : 
                             (typeof stat.value === 'string' && !isNaN(Number(stat.value))) ? Number(stat.value) : 0

            switch (stat.type) {
              case 'Corner Kicks':
                if (isHome) homeCorners = statValue
                else awayCorners = statValue
                break
              case 'Yellow Cards':
                if (isHome) homeYellowCards = statValue
                else awayYellowCards = statValue
                break
              case 'Red Cards':
                if (isHome) homeRedCards = statValue
                else awayRedCards = statValue
                break
            }
          })
        })
      }

      // Use events data when statistics are not available
      if ((!homeYellowCards && !awayYellowCards && !homeRedCards && !awayRedCards) && 
          match.events && match.events.length > 0) {
        match.events.forEach(event => {
          if (event.type === 'Card') {
            const isHome = event.team.id === match.teams.home.id
            
            if (event.detail === 'Yellow Card') {
              if (isHome) homeYellowCards++
              else awayYellowCards++
            }
            
            if (event.detail === 'Red Card' || event.detail === 'Second Yellow card') {
              if (isHome) homeRedCards++
              else awayRedCards++
            }
          }
        })
      }

      return {
        match_id: match.fixture.id,
        match_date: matchDateTime.toISOString().split('T')[0],
        match_time: matchDateTime.toTimeString().slice(0, 5),
        status: match.fixture.status.short,
        competition_id: match.league.id,
        competition_name: match.league.name,
        competition_emblem: match.league.logo,
        home_team_name: match.teams.home.name,
        home_team_crest: match.teams.home.logo,
        away_team_name: match.teams.away.name,
        away_team_crest: match.teams.away.logo,
        home_score: match.goals.home,
        away_score: match.goals.away,
        home_ht_score: match.score?.halftime?.home || null,
        away_ht_score: match.score?.halftime?.away || null,
        priority,
        home_yellow_cards: homeYellowCards,
        away_yellow_cards: awayYellowCards,
        home_red_cards: homeRedCards,
        away_red_cards: awayRedCards,
        home_corners: homeCorners,
        away_corners: awayCorners
      }
    })
  }

  public async getFinishedMatches(
    date: string, 
    league: string | number = 'all', 
    limit: number = 30, 
    page: number = 1
  ): Promise<FootballApiResponse | null> {
    const cacheKey = `football_finished_matches_${date}_${league}_${limit}_${page}`
    
    try {
      // Check cache first
      if (this.redis) {
        const cached = await this.redis.get(cacheKey)
        if (cached) {
          return JSON.parse(cached)
        }
      }

      // Process league ID
      const leagueId = league !== 'all' && typeof league === 'string' && !isNaN(Number(league)) ? 
                       Number(league) : league

      // Calculate dates
      const selectedDate = new Date(date)
      const previousDay = new Date(selectedDate)
      previousDay.setDate(previousDay.getDate() - 1)

      // Get matches for today
      const matchesToday = await this.callApi('fixtures', {
        date: date,
        league: leagueId !== 'all' ? leagueId : undefined,
        timezone: 'Asia/Ho_Chi_Minh',
        status: 'FT-AET-PEN-WO-AWD' // Only finished matches
      })

      // Get matches for yesterday (from 18:00 onwards)
      const matchesYesterday = await this.callApi('fixtures', {
        date: previousDay.toISOString().split('T')[0],
        league: leagueId !== 'all' ? leagueId : undefined,
        timezone: 'Asia/Ho_Chi_Minh',
        status: 'FT-AET-PEN-WO-AWD' // Only finished matches
      })

      // Combine results
      let allMatches: ApiFootballFixture[] = []

      if (matchesToday) {
        allMatches = [...allMatches, ...matchesToday]
      }

      if (matchesYesterday) {
        // Filter yesterday matches to only include those from 18:00 onwards
        const filteredYesterday = matchesYesterday.filter(match => {
          const matchDateTime = new Date(match.fixture.date)
          // API đã trả về múi giờ +7 sẵn
          const matchHour = matchDateTime.getHours()
          return matchHour >= 18
        })
        allMatches = [...allMatches, ...filteredYesterday]
      }

      if (allMatches.length === 0) {
        return null
      }

      // Sort matches by league priority
      allMatches.sort((a, b) => {
        const priorityA = this.getLeaguePriority(a.league.id)
        const priorityB = this.getLeaguePriority(b.league.id)
        return priorityA - priorityB
      })

      // Calculate pagination
      const totalMatches = allMatches.length
      const offset = (page - 1) * limit
      const pagedMatches = allMatches.slice(offset, offset + limit)
      const hasMore = (offset + limit) < totalMatches

      // Get statistics and events for each match
      const matchesWithStats = await Promise.all(
        pagedMatches.map(async (match) => {
          const [statistics, events] = await Promise.all([
            this.getMatchStatistics(match.fixture.id),
            this.getMatchEvents(match.fixture.id)
          ])

          return {
            ...match,
            statistics,
            events
          }
        })
      )

      // Format matches
      const formattedMatches = this.formatMatches(matchesWithStats)

      const result: FootballApiResponse = {
        data: formattedMatches,
        has_more: hasMore,
        total: totalMatches,
        page: page,
        per_page: limit
      }

      // Cache for 1 hour
      if (this.redis) {
        await this.redis.setex(cacheKey, this.cacheTime, JSON.stringify(result))
      }

      return result
    } catch (error) {
      console.error('Error getting finished matches:', error)
      return null
    }
  }

  public async getAllMatches(date: string, league: string | number = 'all') {
    try {
      // Get matches from all categories
      const [finished, upcoming] = await Promise.all([
        this.getFinishedMatches(date, league),
        this.getUpcomingMatches(date, league)
      ])

      return {
        finished: finished?.data || [],
        upcoming: upcoming || [],
        live: upcoming?.filter(match => this.isLiveMatch(match.status)) || []
      }
    } catch (error) {
      console.error('Error getting all matches:', error)
      return null
    }
  }

  // Helper function to check if match is upcoming or live
  private isUpcomingOrLiveMatch(status: string): boolean {
    return isUpcomingOrLiveMatch(status)
  }

  // Helper function to check if match is finished
  private isFinishedMatch(status: string): boolean {
    return isFinishedMatch(status)
  }

  // Helper function to check if match is live
  private isLiveMatch(status: string): boolean {
    return isLiveMatch(status)
  }

  public async getUpcomingMatches(
    date: string,
    league: string | number = 'all',
    limit: number = 20
  ): Promise<FootballMatch[]> {
    try {
      const cacheKey = `upcoming_matches_${date}_${league}_${limit}`
      
      if (this.redis && typeof window === 'undefined') {
        try {
          const cached = await this.redis.get(cacheKey)
          if (cached) {
            return JSON.parse(cached)
          }
        } catch (error) {
          console.warn('Redis get error:', error)
        }
      }

      // Process league ID
      const leagueId = league !== 'all' && typeof league === 'string' && !isNaN(Number(league)) ? 
                       Number(league) : league

      // Calculate dates
      const selectedDate = new Date(date)
      const previousDay = new Date(selectedDate)
      previousDay.setDate(previousDay.getDate() - 1)

      // Get upcoming matches for today
      const matchesToday = await this.callApi('fixtures', {
        date: date,
        league: leagueId !== 'all' ? leagueId : undefined,
        timezone: 'Asia/Ho_Chi_Minh',
        status: 'NS-1H-2H-HT-ET-BT-P-SUSP-INT-LIVE-TBD' // Chỉ lấy trận chưa diễn ra và đang diễn ra
      })

      // Get upcoming matches for yesterday (from 18:00 onwards)
      const matchesYesterday = await this.callApi('fixtures', {
        date: previousDay.toISOString().split('T')[0],
        league: leagueId !== 'all' ? leagueId : undefined,
        timezone: 'Asia/Ho_Chi_Minh',
        status: 'NS-1H-2H-HT-ET-BT-P-SUSP-INT-LIVE-TBD' // Chỉ lấy trận chưa diễn ra và đang diễn ra
      })

      // Combine results
      let allMatches: ApiFootballFixture[] = []

      if (matchesToday) {
        allMatches = [...allMatches, ...matchesToday]
      }

      if (matchesYesterday) {
        // Filter yesterday matches to only include those from 18:00 onwards
        const filteredYesterday = matchesYesterday.filter(match => {
          const matchDateTime = new Date(match.fixture.date)
          // API đã trả về múi giờ +7 sẵn
          const matchHour = matchDateTime.getHours()
          return matchHour >= 18
        })
        allMatches = [...allMatches, ...filteredYesterday]
      }

      if (allMatches.length === 0) {
        return []
      }

      // Format matches
      const formattedMatches = this.formatMatches(allMatches)

      // Filter by status on client side as backup
      const filteredMatches = formattedMatches.filter((match: FootballMatch) => 
        this.isUpcomingOrLiveMatch(match.status)
      )

      // Sort by priority and then by time
      filteredMatches.sort((a, b) => {
        if (a.priority === b.priority) {
          if (a.match_date === b.match_date) {
            return a.match_time.localeCompare(b.match_time)
          }
          return a.match_date.localeCompare(b.match_date)
        }
        return a.priority - b.priority
      })

      // Apply limit
      const result = limit > 0 ? filteredMatches.slice(0, limit) : filteredMatches

      // Cache for 30 minutes (upcoming matches change more frequently)
      if (this.redis && typeof window === 'undefined') {
        try {
          await this.redis.setex(cacheKey, 1800, JSON.stringify(result))
        } catch (error) {
          console.warn('Redis set error:', error)
        }
      }

      return result
    } catch (error) {
      console.error('Error fetching upcoming matches:', error)
      return []
    }
  }

  // Close Redis connection
  public async close(): Promise<void> {
    if (this.redis) {
      await this.redis.quit()
    }
  }

  // Get a specific fixture by ID
  public async getFixtureById(fixtureId: number): Promise<ApiFootballFixture | null> {
    try {
      const cacheKey = `football_fixture_${fixtureId}`
      
      // Check cache first
      if (this.redis) {
        try {
          const cached = await this.redis.get(cacheKey)
          if (cached) {
            return JSON.parse(cached)
          }
        } catch (error) {
          console.warn('Redis get error:', error)
        }
      }

      // Call API to get fixture data
      const response = await this.callApi('fixtures', { id: fixtureId })
      
      if (response && response.length > 0) {
        const fixtureData = response[0]
        
        // Cache for 1 hour
        if (this.redis) {
          try {
            await this.redis.setex(cacheKey, this.cacheTime, JSON.stringify(fixtureData))
          } catch (error) {
            console.warn('Redis set error:', error)
          }
        }
        
        return fixtureData
      }

      return null
    } catch (error) {
      console.error('Error getting fixture by ID:', error)
      return null
    }
  }
}

// Initialize the API handler
let footballApi: FootballApiHandler | null = null

try {
  // Only initialize on server side (Node.js environment)
  if (typeof window === 'undefined') {
    footballApi = new FootballApiHandler()
  }
} catch (error) {
  console.error('Failed to initialize FootballApiHandler:', error)
  footballApi = null
}

export { footballApi }

// Helper function to check if match is upcoming or live
export const isUpcomingOrLiveMatch = (status: string): boolean => {
  const upcomingStatuses = ['TBD', 'NS'] // Chưa diễn ra
  const liveStatuses = ['1H', 'HT', '2H', 'ET', 'BT', 'P', 'SUSP', 'INT', 'LIVE'] // Đang diễn ra
  
  return upcomingStatuses.includes(status) || liveStatuses.includes(status)
}

// Helper function to check if match is finished
export const isFinishedMatch = (status: string): boolean => {
  const finishedStatuses = ['FT', 'AET', 'PEN'] // Đã kết thúc
  return finishedStatuses.includes(status)
}

// Helper function to check if match is live
export const isLiveMatch = (status: string): boolean => {
  const liveStatuses = ['1H', 'HT', '2H', 'ET', 'BT', 'P', 'SUSP', 'INT', 'LIVE']
  return liveStatuses.includes(status)
} 